import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { Heart, ChevronLeft, ChevronRight } from "lucide-react";
import { api } from "../lib/api";
import { useStore } from "../store/StoreContext";
import { img } from "../lib/format";
import { CATEGORY_SKETCH } from "../lib/nav";
import SEO from "../components/SEO";
import ProductCard from "../components/ProductCard";
import FeatureStrip from "../components/FeatureStrip";
import WordReveal from "../components/WordReveal";
import NotFound from "./NotFound";
import EnquiryQuantity from "../components/EnquiryQuantity";
import CatalogueDetails from "../components/CatalogueDetails";
import ExportPrice from "../components/ExportPrice";
import { productPresentation } from "../lib/productPresentation";

export default function ProductPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { addToCart, toggleWishlist, wishlist } = useStore();
  const [product, setProduct] = useState(null);
  const [related, setRelated] = useState([]);
  const [missing, setMissing] = useState(false);
  const [active, setActive] = useState(0);
  const [qty, setQty] = useState("");
  const reduced = useReducedMotion();
  const [showSideImages, setShowSideImages] = useState(() => window.matchMedia?.("(min-width: 1024px)").matches === true);

  useEffect(() => {
    const query = window.matchMedia?.("(min-width: 1024px)");
    const update = () => setShowSideImages(query?.matches === true);
    if (query?.addEventListener) query.addEventListener("change", update);
    else query?.addListener?.(update);
    return () => {
      if (query?.removeEventListener) query.removeEventListener("change", update);
      else query?.removeListener?.(update);
    };
  }, []);

  useEffect(() => {
    let current = true;
    setProduct(null);
    setMissing(false);
    setActive(0);
    setQty("");
    setRelated([]);
    api.product(slug).then((data) => { if (current) setProduct(data); }).catch(() => { if (current) setMissing(true); });
    api.related(slug).then((data) => { if (current) setRelated(data); }).catch(() => {});
    return () => { current = false; };
  }, [slug]);

  if (missing) return <NotFound />;
  if (!product) {
    return (
      <div className="-mt-[76px] bg-beige h-[70vh] animate-pulse" />
    );
  }

  const presentation = productPresentation(product);
  const images = presentation.images.map(img);
  const len = images.length;
  const prev = () => setActive((a) => (a - 1 + len) % len);
  const next = () => setActive((a) => (a + 1) % len);
  const wished = wishlist.includes(product.slug);
  const lifestyle = presentation.lifestyle.map(img);
  const sketch = CATEGORY_SKETCH[product.category];

  const enquireNow = () => {
    addToCart(product, qty, true);
    navigate("/enquiry");
  };

  const imgCls = (i) => (i === 0 ? "object-contain cutout-shadow" : "object-cover rounded-[28px]");

  return (
    <div data-testid="product-page" className="pb-32">
      <SEO
        title={product.seo_title || `${product.name} — S H & SONS ®`}
        description={product.seo_description || product.description || `${product.name} for international sourcing. MOQ 500 pieces; specifications are confirmed with our export team.`}
        image={images[0]}
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Product",
          name: product.name,
          ...(product.sku ? { sku: product.sku } : {}),
          image: images,
          description: product.seo_description || product.description || "MOQ 500 pieces. Specifications are confirmed with our export team.",
          brand: { "@type": "Brand", name: "S H & SONS ®" },
        }}
      />

      <section className="relative bg-beige -mt-[76px] pt-[96px] pb-[150px] overflow-hidden" data-testid="product-gallery-main">
        <div className="relative mx-auto max-w-[1400px] px-4 h-[360px] sm:h-[440px] lg:h-[520px] flex items-center justify-center">
          {showSideImages && len > 1 && (
            <img
              src={images[(active - 1 + len) % len]}
              alt=""
              aria-hidden="true"
              loading="lazy"
              decoding="async"
              fetchPriority="low"
              className={`hidden lg:block absolute left-[6%] top-1/2 -translate-y-1/2 h-[58%] w-[22%] opacity-30 ${imgCls((active - 1 + len) % len)}`}
            />
          )}
          <AnimatePresence mode="wait" initial={false}>
            {len > 0 &&
            <motion.img
              key={active}
              src={images[active]}
              alt={`${presentation.alt} — catalogue image ${active + 1}`}
              loading="eager"
              decoding="async"
              fetchPriority="high"
              initial={reduced ? false : { opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={reduced ? { opacity: 1 } : { opacity: 0, scale: 0.98 }}
              transition={{ duration: reduced ? 0 : 0.4 }}
              className={`h-full max-w-[82%] lg:max-w-[46%] ${imgCls(active)}`}
              data-testid="product-gallery-image"
            />}
          </AnimatePresence>
          {showSideImages && len > 1 && (
            <img
              src={images[(active + 1) % len]}
              alt=""
              aria-hidden="true"
              loading="lazy"
              decoding="async"
              fetchPriority="low"
              className={`hidden lg:block absolute right-[6%] top-1/2 -translate-y-1/2 h-[58%] w-[22%] opacity-30 ${imgCls((active + 1) % len)}`}
            />
          )}

          {len > 1 && (
            <>
              <button type="button" data-testid="product-gallery-prev" onClick={prev} aria-label="Previous image" className="absolute left-2 sm:left-8 lg:left-[22%] top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-ink text-white flex items-center justify-center hover:bg-charcoal transition-colors duration-200 shadow-lg">
                <ChevronLeft className="w-4 h-4" strokeWidth={1.5} />
              </button>
              <button type="button" data-testid="product-gallery-next" onClick={next} aria-label="Next image" className="absolute right-2 sm:right-8 lg:right-[22%] top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-ink text-white flex items-center justify-center hover:bg-charcoal transition-colors duration-200 shadow-lg">
                <ChevronRight className="w-4 h-4" strokeWidth={1.5} />
              </button>
            </>
          )}
        </div>

        <div className="mt-6 flex flex-wrap justify-center gap-0.5 px-4" data-testid="product-gallery-dots">
          {images.map((_, i) => (
            <button
              key={i}
              type="button"
              data-testid={`product-thumbnail-${i}`}
              onClick={() => setActive(i)}
              aria-label={`Show image ${i + 1}`}
              aria-pressed={active === i}
              className="w-11 h-11 rounded-full flex items-center justify-center group"
            ><span aria-hidden="true" className={`w-2 h-2 rounded-full transition-colors duration-200 ${active === i ? "bg-ink" : "bg-ink/25 group-hover:bg-ink/50"}`} /></button>
          ))}
        </div>

        <div className="absolute right-5 sm:right-10 top-1/2 -translate-y-1/2 flex flex-col items-center gap-3">
          <button
            type="button"
            data-testid="product-wishlist-button"
            onClick={() => toggleWishlist(product)}
            aria-label={wished ? "Remove from wishlist" : "Add to wishlist"}
            aria-pressed={wished}
            className="w-11 h-11 rounded-full bg-white shadow-md flex items-center justify-center hover:text-brass transition-colors duration-200"
          >
            <Heart className={`w-4 h-4 ${wished ? "fill-brass text-brass" : ""}`} strokeWidth={1.5} />
          </button>
        </div>
      </section>

      <section className="relative -mt-[120px] z-10 px-3">
        <div
          className="mx-auto max-w-[1100px] bg-white pt-14 sm:pt-20 pb-6 px-6 text-center"
          style={{ borderRadius: "50% 50% 0 0 / 140px 140px 0 0" }}
          data-testid="product-info-panel"
        >
          <p className="eyebrow mb-3">{product.category}</p>
          <h1 className="text-3xl sm:text-[40px] font-light text-brass" data-testid="product-title">{product.name}</h1>
          <p className="mt-3 text-[12px] text-mocha">
            Crafted by <Link to="/about#craftsmanship" className="underline underline-offset-4 text-ink hover:text-brass"><span className="brand-name">S H &amp; SONS ®</span>, Moradabad</Link>
          </p>
          <p className="mt-6 text-[14px] text-mocha">{product.collections?.includes("christmas-2027") ? "Christmas Collection 2027 · Customisation available" : "Design preview · final selection pending"}</p>
          <CatalogueDetails product={product} dimensions className="mt-5 mx-auto max-w-lg" />
          <ExportPrice product={product} />
          <div className="mt-8 inline-flex flex-col items-center">
            <EnquiryQuantity value={qty} onChange={setQty} id="product-quantity" />
            <p className="mt-3 text-[12px] text-mocha">MOQ 500 pieces. Quantities are confirmed with our team.</p>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-2xl px-6 pt-10 pb-6 text-center" data-testid="product-story">
        {sketch && <img src={sketch} alt={`${product.category} sketch`} className="h-28 sm:h-36 mx-auto opacity-80 mb-10" loading="lazy" decoding="async" />}
        <p className="text-[14px] sm:text-[15px] text-charcoal leading-[1.8] whitespace-pre-line">{presentation.description || presentation.shortDescription || "Explore this design direction for your collection. Final product selection and specifications will be confirmed with our team."}</p>
        <h2 className="mt-14 text-2xl sm:text-[30px] font-light">Develop your collection</h2>
        <p className="mt-5 text-[14px] sm:text-[15px] text-charcoal leading-[1.8] whitespace-pre-line">{presentation.details || "Share your preferred materials, finishes and development requirements for a tailored discussion."}</p>
        <p className="mt-6 text-[13px] text-mocha">Connect with the <span className="brand-name">S H &amp; SONS ®</span> team in Moradabad.</p>
      </section>

      {lifestyle.length > 0 && (
        <section className="py-10" data-testid="styled-in-situ">
          <div className="flex gap-4 sm:gap-6 overflow-x-auto no-scrollbar px-4 sm:px-8 snap-x">
            {lifestyle.map((src, i) => (
              <img key={src} src={src} alt={`${presentation.alt} — additional catalogue image ${i + 1}`} loading="lazy" decoding="async" className="h-[300px] sm:h-[440px] w-auto max-w-[85vw] object-cover rounded-[28px] shrink-0 snap-center" />
            ))}
          </div>
        </section>
      )}

      <section className="mx-auto max-w-3xl px-6 py-16" data-testid="product-technical-details">
        <h2 className="text-center text-2xl sm:text-[30px] font-light mb-10">Export Enquiries</h2>
        <dl className="border-t border-line">
          {[
            ...presentation.attributes,
            ["Product selection", "References shortlisted from our supplied catalogues. Final selection is pending."],
            ["Specifications", "Materials, finishes and dimensions are confirmed for each enquiry."],
            ["Minimum quantities", "MOQ 500 pieces"],
            ["Export enquiries", "Share your market, finish and approximate requirements. Our team will confirm specifications, quantities and timelines."],
          ].map(([k, v]) => (
            <div key={k} className="grid sm:grid-cols-[220px_1fr] gap-2 sm:gap-8 py-5 border-b border-line" data-testid={`tech-${k.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
              <dt className="text-[11px] uppercase tracking-[0.2em] text-mocha pt-0.5">{k}</dt>
              <dd className="text-[14px] text-charcoal leading-relaxed whitespace-pre-line break-words">{v}</dd>
            </div>
          ))}
        </dl>
      </section>

      <FeatureStrip />

      <section className="mx-auto max-w-3xl px-6 py-16 text-center" data-testid="about-maker">
        <img src="/assets/monogram-black.svg" alt="S H & SONS ® monogram" className="h-14 mx-auto mb-8" />
        <p className="eyebrow mb-4">About the maker</p>
        <WordReveal text="S H & SONS ®, Moradabad" className="h-display text-3xl sm:text-4xl lg:text-5xl" />
        <p className="mt-6 text-[14px] text-charcoal leading-[1.8]">
          A manufacturer and exporter from India's metal capital, <span className="brand-name">S H &amp; SONS ®</span> has produced for the world's home brands for decades. Our team works with international buyers on collections, finishes and project requirements.
        </p>
        <p className="mt-6 text-[13px] text-mocha">
          Explore other designs crafted by <Link to="/shop" className="underline underline-offset-4 text-ink hover:text-brass"><span className="brand-name">S H &amp; SONS ®</span></Link>
        </p>
      </section>

      {related.length > 0 && (
        <section className="mx-auto max-w-[1400px] px-4 sm:px-8 py-16" data-testid="related-products">
          <h2 className="text-center text-2xl sm:text-[30px] font-light mb-12">You may also like</h2>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-12">
            {related.map((p, i) => (
              <ProductCard key={p.slug} product={p} index={i} />
            ))}
          </div>
        </section>
      )}

      <div className="fixed bottom-5 inset-x-0 z-40 flex justify-center gap-2 px-4 pointer-events-none" data-testid="pdp-sticky-bar">
        <button
          type="button"
          data-testid="add-to-enquiry-button"
          onClick={() => addToCart(product, qty)}
          className="pill-dark min-h-11 pointer-events-auto px-5 sm:px-12 py-4 uppercase tracking-[0.2em] text-[11px] shadow-2xl"
        >
          Add to enquiry
        </button>
        <button
          type="button"
          data-testid="enquire-now-button"
          onClick={enquireNow}
          className="pill-gold min-h-11 pointer-events-auto px-6 sm:px-12 py-4 shadow-2xl"
        >
          Enquire now
        </button>
      </div>
    </div>
  );
}
